import type { AudioDynamicsSetting, GameSettings } from '../settings.js';
import { t } from '../i18n/index.js';

export type AudioMixChannel = 'master' | 'effects' | 'music' | 'ui';

const CHANNELS: readonly { id: AudioMixChannel; label: string; hint: string; key: keyof GameSettings }[] = [
  { id: 'master', label: 'MASTER', hint: 'Everything you hear', key: 'masterVolume' },
  { id: 'effects', label: 'ENGINE + EFFECTS', hint: 'Engines, tyres, impacts, weather', key: 'effectsVolume' },
  { id: 'music', label: 'MUSIC', hint: 'Adaptive race score and menu theme', key: 'musicVolume' },
  { id: 'ui', label: 'INTERFACE', hint: 'Menu clicks, rewards and race calls', key: 'uiVolume' },
];

const DYNAMICS: readonly { id: AudioDynamicsSetting; label: string; hint: string }[] = [
  { id: 'wide', label: 'WIDE', hint: 'Full punch for headphones and home cinema.' },
  { id: 'standard', label: 'STANDARD', hint: 'Balanced for most speakers and headsets.' },
  { id: 'night', label: 'NIGHT', hint: 'Evens out loud crashes; clearer on laptops and late at night.' },
];

export const VU_SEGMENTS = 14;

/**
 * The mixer used both in the pause/settings sheet and the menu's quick sound
 * panel. Controls are addressed by data attributes, so any number of mixers
 * can be on screen and a single delegated handler drives them all.
 */
export function audioMixerHtml(variant: 'full' | 'compact'): string {
  const compact = variant === 'compact';
  return `
    <div class="audio-mixer ${compact ? 'compact' : ''}" data-audio-mixer>
      <div class="audio-mixer-head">
        <button type="button" class="audio-power" data-audio-mute aria-pressed="true">
          <i class="audio-speaker" aria-hidden="true"></i>
          <span><b data-audio-mute-label>${t('SOUND ON')}</b><small>${t('Press M in a race to mute')}</small></span>
        </button>
        <div class="audio-vu" data-audio-vu role="meter" aria-label="${t('Output level')}" aria-valuemin="-60" aria-valuemax="0" aria-valuenow="-60">
          ${Array.from({ length: VU_SEGMENTS }, () => '<i></i>').join('')}
        </div>
      </div>
      <div class="audio-faders">
        ${CHANNELS.map((channel) => `
          <label class="audio-fader" data-audio-channel="${channel.id}">
            <span class="audio-fader-label"><b>${t(channel.label)}</b>${compact ? '' : `<small>${t(channel.hint)}</small>`}</span>
            <output data-audio-value="${channel.id}">100%</output>
            <input type="range" min="0" max="1" step="0.05" value="1" data-audio-mix="${channel.id}" aria-label="${t('{channel} volume', { channel: t(channel.label).toLowerCase() })}">
          </label>`).join('')}
      </div>
      <div class="audio-dynamics">
        <span class="audio-dynamics-label"><b>${t('DYNAMIC RANGE')}</b><small data-audio-dynamics-hint></small></span>
        <div class="audio-dynamics-options" role="radiogroup" aria-label="${t('Dynamic range')}">
          ${DYNAMICS.map((option) => `<button type="button" role="radio" aria-checked="false" data-audio-dynamics="${option.id}" title="${t(option.hint)}">${t(option.label)}</button>`).join('')}
        </div>
      </div>
      ${compact ? '' : `<button type="button" class="audio-test" data-audio-test><i aria-hidden="true"></i>${t('PLAY TEST SOUND')}</button>`}
    </div>`;
}

export function audioChannelSettingKey(channel: AudioMixChannel): 'masterVolume' | 'effectsVolume' | 'musicVolume' | 'uiVolume' {
  return CHANNELS.find((entry) => entry.id === channel)!.key as 'masterVolume' | 'effectsVolume' | 'musicVolume' | 'uiVolume';
}

/** Reflects settings and mute state into every mixer under `root`. */
export function syncAudioMixers(root: ParentNode, settings: GameSettings, muted: boolean): void {
  for (const channel of CHANNELS) {
    const value = settings[channel.key] as number;
    const percent = `${Math.round(value * 100)}%`;
    root.querySelectorAll<HTMLInputElement>(`[data-audio-mix="${channel.id}"]`).forEach((input) => {
      if (document.activeElement !== input) input.value = String(value);
      input.closest<HTMLElement>('.audio-fader')?.style.setProperty('--level', `${value * 100}%`);
    });
    root.querySelectorAll<HTMLOutputElement>(`[data-audio-value="${channel.id}"]`).forEach((output) => {
      output.textContent = value <= 0 ? t('OFF') : percent;
    });
  }
  const dynamics = DYNAMICS.find((option) => option.id === settings.audioDynamics) ?? DYNAMICS[1]!;
  root.querySelectorAll<HTMLButtonElement>('[data-audio-dynamics]').forEach((button) => {
    button.setAttribute('aria-checked', String(button.dataset.audioDynamics === dynamics.id));
  });
  root.querySelectorAll<HTMLElement>('[data-audio-dynamics-hint]').forEach((hint) => {
    hint.textContent = t(dynamics.hint);
  });
  root.querySelectorAll<HTMLButtonElement>('[data-audio-mute]').forEach((button) => {
    button.setAttribute('aria-pressed', String(!muted));
    const label = button.querySelector('[data-audio-mute-label]');
    if (label) label.textContent = muted ? t('SOUND OFF') : t('SOUND ON');
  });
  root.querySelectorAll<HTMLElement>('[data-audio-mixer]').forEach((mixer) => {
    mixer.dataset.muted = String(muted);
  });
  document.documentElement.dataset.audioMuted = String(muted);
}

/**
 * Drives the segment meters from the real output while a mixer is visible.
 * Peak-hold with a slow fall, like hardware meters.
 */
export class AudioVuMeter {
  private frame = 0;
  private level = -60;

  constructor(
    private readonly root: ParentNode,
    private readonly read: () => { rmsDb: number; peakDb: number } | null,
  ) {}

  start(): void {
    if (this.frame || typeof requestAnimationFrame === 'undefined') return;
    const tick = () => {
      this.frame = requestAnimationFrame(tick);
      this.render();
    };
    this.frame = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.level = -60;
    this.render(true);
  }

  private render(reset = false): void {
    const meters = this.root.querySelectorAll<HTMLElement>('[data-audio-vu]');
    if (meters.length === 0) return;
    const reading = reset ? null : this.read();
    const target = reading ? Math.max(reading.rmsDb + 6, reading.peakDb - 4) : -60;
    this.level = target > this.level ? target : this.level - 1.2;
    const lit = Math.round(Math.max(0, Math.min(1, (this.level + 54) / 54)) * VU_SEGMENTS);
    meters.forEach((meter) => {
      meter.setAttribute('aria-valuenow', String(Math.round(this.level)));
      const segments = meter.children;
      for (let index = 0; index < segments.length; index += 1) {
        (segments[index] as HTMLElement).classList.toggle('on', index < lit);
      }
    });
  }
}
